// Gmail content scripts - injected into page context via chrome.scripting.executeScript.
//
// IMPORTANT: Each exported function must be completely self-contained. They cannot
// reference each other or any module-scope variables because executeScript serializes
// each function independently and runs it in the page context where nothing else exists.
// This is why the visibility/selection logic is duplicated between the two exports.

/**
 * Finds the active Gmail message ID from visible message containers.
 * Returns the message ID of the largest visible message pane.
 */
export function findActiveGmailMessageId(): string | undefined {
  // NOTE: here's the previous single-line approach,
  // which was working only for the Inbox folder, and didn't get the email ID correctly for all cases:
  // `document.querySelector("div[data-message-id]")?.getAttribute("data-message-id")`

  const SELECTOR = 'div.adn.ads[data-message-id^="#msg-"]';

  const isVisible = (el: Element): boolean => {
    const e = el as HTMLElement;
    if (!e.isConnected) return false;
    const style = window.getComputedStyle(e);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
      return false;
    }
    const r = e.getBoundingClientRect();
    return r.width > 5 && r.height > 5;
  };

  const nodes = Array.from(document.querySelectorAll<HTMLElement>(SELECTOR));
  const visible = nodes.filter(isVisible);
  if (visible.length === 0) return undefined;

  let best = visible[0];
  let bestArea = 0;
  for (const el of visible) {
    const r = el.getBoundingClientRect();
    const area = r.width * r.height;
    if (area > bestArea) {
      bestArea = area;
      best = el;
    }
  }

  return best.getAttribute("data-message-id") ?? undefined;
}

/**
 * Sets up a MutationObserver to detect Gmail message selection changes.
 * Sends GMAIL_SELECTION_CHANGED message when the active message changes.
 */
export function setupGmailSelectionObserver(): void {
  const win = window as unknown as { __meridianObserver?: boolean };
  if (win.__meridianObserver) return;
  win.__meridianObserver = true;

  const SELECTOR = 'div.adn.ads[data-message-id^="#msg-"]';

  const isVisible = (el: Element): boolean => {
    const e = el as HTMLElement;
    if (!e.isConnected) return false;
    const style = window.getComputedStyle(e);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
      return false;
    }
    const r = e.getBoundingClientRect();
    return r.width > 5 && r.height > 5;
  };

  const getActiveMessageId = (): string | null => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>(SELECTOR));
    const visible = nodes.filter(isVisible);
    if (visible.length === 0) return null;

    let best = visible[0];
    let bestArea = 0;
    for (const el of visible) {
      const r = el.getBoundingClientRect();
      const area = r.width * r.height;
      if (area > bestArea) {
        bestArea = area;
        best = el;
      }
    }
    return best.getAttribute("data-message-id");
  };

  let lastMessageId: string | null = null;

  const checkMessageId = () => {
    const currentId = getActiveMessageId();
    if (currentId && currentId !== lastMessageId) {
      lastMessageId = currentId;
      void chrome.runtime.sendMessage({ type: "GMAIL_SELECTION_CHANGED", messageId: currentId });
    }
  };

  checkMessageId();

  const observer = new MutationObserver(checkMessageId);
  observer.observe(document.body, { childList: true, subtree: true, attributes: true });
}